import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, UserCog, Building2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: () => api.get("/staff").then((r) => r.data), enabled: isAdmin });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api.get("/units").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [resetForm, setResetForm] = useState(null);

  const doReset = async () => {
    if (!resetForm.password || resetForm.password.length < 8) { toast.error(t("account.tooShort")); return; }
    try {
      await api.post(`/staff/${resetForm.id}/reset-password`, { new_password: resetForm.password });
      setResetForm(null); toast.success(t("set.resetPwSuccess"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const saveStaff = async () => {
    if (!form.name || !form.email || !form.password) { toast.error(t("set.name")); return; }
    try {
      await api.post("/staff", form);
      qc.invalidateQueries({ queryKey: ["staff"] });
      setForm(null); toast.success(t("set.addStaff"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeStaff = async (id) => {
    if (!window.confirm(t("set.deleteStaff"))) return;
    try { await api.delete(`/staff/${id}`); qc.invalidateQueries({ queryKey: ["staff"] }); toast.success(t("common.delete")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const updateUnitField = async (u, field, val) => {
    await api.put(`/units/${u.id}`, { [field]: Number(val) });
    qc.invalidateQueries({ queryKey: ["units"] });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("set.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("set.subtitle")}</p>
      </div>

      {isAdmin && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-teak flex items-center gap-2"><UserCog className="w-5 h-5" />{t("set.staff")}</h2>
            <Button onClick={() => setForm({ name: "", email: "", password: "", role: "staff" })} className="bg-teak hover:bg-teak-hover text-white" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" />{t("set.addStaff")}</Button>
          </div>
          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>{t("set.name")}</TableHead><TableHead>{t("set.email")}</TableHead><TableHead>{t("set.role")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.id} className="hover:bg-[#FDFDFB]" data-testid={`staff-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name} {s.id === user.id && <span className="text-xs text-muted-foreground">({t("set.you")})</span>}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell><Badge className={s.role === "admin" ? "bg-teak" : ""} variant={s.role === "admin" ? "default" : "secondary"}>{t(`set.${s.role === "admin" ? "admin" : s.role === "reception" ? "reception" : "staffRole"}`)}</Badge></TableCell>
                    <TableCell className="text-right">{s.id !== user.id && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title={t("set.resetPw")} onClick={() => setResetForm({ id: s.id, name: s.name, password: "" })} data-testid={`staff-reset-${s.id}`}><KeyRound className="w-4 h-4 text-teak" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => removeStaff(s.id)} data-testid={`staff-delete-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    )}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-teak flex items-center gap-2"><Building2 className="w-5 h-5" />{t("set.units")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {units.map((u) => (
            <Card key={u.id} className="p-4 border-border" data-testid={`settings-unit-${u.number}`}>
              <div className="flex items-center justify-between mb-3"><span className="font-semibold text-lg">{u.number}</span><span className="text-xs text-muted-foreground">{u.name}</span></div>
              <div className="space-y-2">
                <div><Label className="text-xs">{t("unit.rentMonth")}</Label><Input type="number" defaultValue={u.base_rent} onBlur={(e) => updateUnitField(u, "base_rent", e.target.value)} className="mt-1" data-testid={`unit-rent-${u.number}`} /></div>
                <div><Label className="text-xs">{t("unit.rentWeek")}</Label><Input type="number" defaultValue={u.price_weekly || 0} onBlur={(e) => updateUnitField(u, "price_weekly", e.target.value)} className="mt-1" data-testid={`unit-week-${u.number}`} /></div>
                <div><Label className="text-xs">{t("unit.rentDay")}</Label><Input type="number" defaultValue={u.price_daily || 0} onBlur={(e) => updateUnitField(u, "price_daily", e.target.value)} className="mt-1" data-testid={`unit-day-${u.number}`} /></div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("set.addStaff")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{t("set.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
              <div className="space-y-2"><Label>{t("set.email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
              <div className="space-y-2"><Label>{t("set.password")}</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" /></div>
              <div className="space-y-2"><Label>{t("set.role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="staff">{t("set.staffRole")}</SelectItem><SelectItem value="reception">{t("set.reception")}</SelectItem><SelectItem value="admin">{t("set.admin")}</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveStaff} className="bg-teak hover:bg-teak-hover text-white" data-testid="staff-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetForm} onOpenChange={(o) => !o && setResetForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("set.resetPw")}</DialogTitle></DialogHeader>
          {resetForm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("set.newPwFor")} <strong className="text-teak">{resetForm.name}</strong></p>
              <div className="space-y-2"><Label>{t("account.new")}</Label><Input type="text" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} data-testid="reset-password-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={doReset} className="bg-teak hover:bg-teak-hover text-white" data-testid="reset-password-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
